import React, { useState } from 'react';
import { Heart, Users, MessageCircle, Settings, Bell, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CoRegulateLogo, CoRegulateMark } from '../components/Logo';

export default function ComponentLibrary() {
  const [switchValue, setSwitchValue] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  return (
    <div className="min-h-screen bg-transparent" data-testid="component-library">
      <div className="bg-white/70 border-b border-white/40 py-12">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <CoRegulateLogo size="lg" showTagline className="justify-center mb-6" />
            <h1 className="font-heading font-semibold tracking-tight text-[#1D6B6B] text-3xl md:text-4xl mb-4">Component Library</h1>
            <p className="text-base text-[#2A8585] leading-relaxed text-lg">Gentle, trauma-informed UI components designed for ADHD hearts</p>
          </div>
        </div>
      </div>

      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <section className="py-12">
          <h2 className="font-heading font-semibold tracking-tight text-[#1D6B6B] text-2xl md:text-3xl mb-6">Color Palette</h2>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {[
              { name: 'Sage Green', color: '#7D8E95' },
              { name: 'Eucalyptus', color: '#7D8E95' },
              { name: 'Mist Blue', color: '#7D8E95' },
              { name: 'Warm Oat', color: '#FBE0C3' },
              { name: 'Soft Sand', color: '#FFBB98' },
              { name: 'Warm Grey', color: '#7D8E95' },
              { name: 'Dusty Lavender', color: '#7D8E95' },
              { name: 'Muted Rose', color: '#FFBB98' },
            ].map((c) => (
              <div key={c.name} className="text-center">
                <div className="h-20 rounded-xl mb-2 shadow-sm border border-white/40/40" style={{ backgroundColor: c.color }} />
                <span className="text-sm text-[#7D8E95]">{c.name}</span>
              </div>
            ))}
          </div>
        </section>

        <hr className="border-none h-px w-full bg-[#FFBB98] my-6" />

        <section className="py-12">
          <h2 className="font-heading font-semibold tracking-tight text-[#1D6B6B] text-2xl md:text-3xl mb-6">Buttons</h2>
          <div className="flex flex-col gap-6">
            <div>
              <label className="block font-medium text-sm text-[#1D6B6B] mb-4">Primary Buttons</label>
              <div className="flex items-center gap-4 flex-wrap">
                <button className="inline-flex items-center justify-center gap-2 font-medium rounded-xl h-11 px-6 text-base bg-[#7D8E95] text-[#1D6B6B] hover:bg-[#9FBA96] transition-all duration-300">Sage Primary</button>
                <button className="inline-flex items-center justify-center gap-2 font-medium rounded-xl h-11 px-6 text-base bg-[#7D8E95] text-[#1D6B6B] hover:bg-[#7FB08F] transition-all duration-300">Eucalyptus Primary</button>
              </div>
            </div>
            <div>
              <label className="block font-medium text-sm text-[#1D6B6B] mb-4">Secondary Buttons</label>
              <div className="flex items-center gap-4 flex-wrap">
                <button className="inline-flex items-center justify-center gap-2 font-medium rounded-xl h-11 px-6 text-base bg-[#7D8E95] text-[#2A8585] border border-[#B9CCDB]/50 hover:bg-[#BFD4E5] transition-all duration-300">Secondary</button>
              </div>
            </div>
            <div>
              <label className="block font-medium text-sm text-[#1D6B6B] mb-4">Icon Buttons</label>
              <div className="flex items-center gap-4 flex-wrap">
                <button className="w-11 h-11 rounded-full flex items-center justify-center bg-transparent text-[#2A8585] hover:bg-[#FFBB98] transition-all duration-300"><Heart size={20} strokeWidth={1.5} /></button>
                <button className="w-11 h-11 rounded-full flex items-center justify-center bg-[#FFBB98] text-[#2A8585] hover:bg-[#FFBB98] transition-all duration-300"><Settings size={20} strokeWidth={1.5} /></button>
                <button className="w-11 h-11 rounded-full flex items-center justify-center bg-transparent text-[#2A8585] border border-[#7D8E95] hover:bg-transparent transition-all duration-300"><Bell size={20} strokeWidth={1.5} /></button>
              </div>
            </div>
          </div>
        </section>

        <hr className="border-none h-px w-full bg-[#FFBB98] my-6" />

        <section className="py-12">
          <h2 className="font-heading font-semibold tracking-tight text-[#1D6B6B] text-2xl md:text-3xl mb-6">Cards</h2>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
            <div className="rounded-2xl p-6 bg-transparent border border-white/40 shadow-[0_4px_20px_rgba(52,70,72,0.08)]">
              <h4 className="font-heading font-semibold text-[#1D6B6B] text-lg mb-2">Default Card</h4>
              <p className="text-base text-[#2A8585]">Soft background with gentle shadow</p>
            </div>
            <div className="rounded-2xl p-6 bg-white/70 border border-white/40 shadow-[0_8px_30px_rgba(52,70,72,0.1)]">
              <h4 className="font-heading font-semibold text-[#1D6B6B] text-lg mb-2">Elevated Card</h4>
              <p className="text-base text-[#2A8585]">More prominent shadow</p>
            </div>
            <div className="rounded-2xl p-6 bg-[#FFBB98] border border-white/40">
              <h4 className="font-heading font-semibold text-[#1D6B6B] text-lg mb-2">Sand Card</h4>
              <p className="text-base text-[#2A8585]">Warmer background tone</p>
            </div>
          </div>
        </section>

        <hr className="border-none h-px w-full bg-[#FFBB98] my-6" />

        <section className="py-12">
          <h2 className="font-heading font-semibold tracking-tight text-[#1D6B6B] text-2xl md:text-3xl mb-6">Selection Cards</h2>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
            {['Overstimulated', 'Anxious', 'Disconnected'].map((option) => (
              <button
                key={option}
                onClick={() => setSelectedCard(option)}
                className={cn(
                  'w-full text-left rounded-2xl p-6 border-2 transition-all duration-400',
                  selectedCard === option
                    ? 'bg-[rgba(52,70,72,0.1)] border-[#7D8E95]'
                    : 'bg-white/70 border-white/40 hover:border-[rgba(52,70,72,0.4)] hover:bg-transparent'
                )}
              >
                <h4 className="font-heading font-semibold text-[#1D6B6B] text-lg mb-1">{option}</h4>
                <span className="text-sm text-[#7D8E95]">Tap to select this state</span>
              </button>
            ))}
          </div>
        </section>

        <hr className="border-none h-px w-full bg-[#FFBB98] my-6" />

        <section className="py-12">
          <h2 className="font-heading font-semibold tracking-tight text-[#1D6B6B] text-2xl md:text-3xl mb-6">Input Fields</h2>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <div className="w-full">
              <label className="block text-sm font-medium text-[#1D6B6B] mb-2">Standard Input</label>
              <input
                type="text"
                className="w-full h-12 px-4 rounded-xl bg-transparent border-2 border-transparent text-[#1D6B6B] placeholder:text-[#2A8585]/60 transition-all duration-400 focus:outline-none focus:border-[rgba(52,70,72,0.5)] focus:bg-white/70"
                placeholder="Enter your thoughts..."
              />
              <p className="mt-2 text-sm text-[#2A8585]">Gentle, rounded styling</p>
            </div>
            <div className="w-full">
              <label className="block text-sm font-medium text-[#1D6B6B] mb-2">With Error</label>
              <input
                type="text"
                className="w-full h-12 px-4 rounded-xl bg-transparent border-2 border-white/40 text-[#1D6B6B] placeholder:text-[#2A8585]/60 transition-all duration-400 focus:outline-none focus:bg-white/70"
                placeholder="Something went wrong"
              />
              <p className="mt-2 text-sm text-[#FFBB98]">This field needs your attention</p>
            </div>
          </div>
        </section>

        <hr className="border-none h-px w-full bg-[#FFBB98] my-6" />

        <section className="py-12">
          <h2 className="font-heading font-semibold tracking-tight text-[#1D6B6B] text-2xl md:text-3xl mb-6">Toggle Switch</h2>
          <label className="flex items-start gap-3 cursor-pointer">
            <button
              type="button"
              role="switch"
              aria-checked={switchValue}
              onClick={() => setSwitchValue(!switchValue)}
              className={cn('relative inline-flex items-center shrink-0 w-11 h-6 rounded-full transition-colors duration-500', switchValue ? 'bg-[#7D8E95]' : 'bg-[#FFBB98]')}
            >
              <span className={cn('absolute left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-500', switchValue ? 'translate-x-5' : 'translate-x-0')} />
            </button>
            <div className="flex-1">
              <span className="block text-sm font-medium text-[#1D6B6B]">Enable notifications</span>
              <span className="block text-sm text-[#2A8585] mt-0.5">Receive gentle reminders to check in</span>
            </div>
          </label>
        </section>

        <hr className="border-none h-px w-full bg-[#FFBB98] my-6" />

        <section className="py-12">
          <h2 className="font-heading font-semibold tracking-tight text-[#1D6B6B] text-2xl md:text-3xl mb-6">Progress Indicators</h2>
          <div className="flex flex-col gap-6">
            <div className="w-full">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-[#2A8585]">Session progress</span>
                <span className="text-sm font-medium text-[#1D6B6B]">65%</span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden bg-[#FFBB98]">
                <div className="h-full rounded-full bg-[#7D8E95]" style={{ width: '65%' }} />
              </div>
            </div>
            <div className="flex items-center justify-center gap-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={cn(
                    'rounded-full transition-all duration-500',
                    index === currentStep ? 'w-3.5 h-3.5 bg-[#7D8E95]' : index < currentStep ? 'w-2.5 h-2.5 bg-[#7D8E95]/50' : 'w-2.5 h-2.5 bg-[#FFBB98]'
                  )}
                />
              ))}
            </div>
          </div>
        </section>

        <hr className="border-none h-px w-full bg-[#FFBB98] my-6" />

        <section className="py-12">
          <h2 className="font-heading font-semibold tracking-tight text-[#1D6B6B] text-2xl md:text-3xl mb-6">Alerts</h2>
          <div className="flex flex-col gap-4">
            <div role="alert" className="relative rounded-xl p-4 border bg-[rgba(125,142,149,0.15)] border-[rgba(125,142,149,0.3)]">
              <div className="flex gap-3">
                <div className="shrink-0 mt-0.5 text-[#1D6B6B]"><Heart size={20} strokeWidth={1.5} /></div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm mb-1 text-[#1D6B6B]">Success</h4>
                  <div className="text-sm text-[#2A8585]">You&apos;ve completed 3 grounding exercises this week.</div>
                </div>
              </div>
            </div>
            <div role="alert" className="relative rounded-xl p-4 border bg-[rgba(125,142,149,0.2)] border-[rgba(125,142,149,0.4)]">
              <div className="flex gap-3">
                <div className="shrink-0 mt-0.5 text-[#7D8E95]"><Heart size={20} strokeWidth={1.5} /></div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm mb-1 text-[#1D6B6B]">Info</h4>
                  <div className="text-sm text-[#2A8585]">Try breathing exercises when you feel overwhelmed.</div>
                </div>
              </div>
            </div>
            <div role="alert" className="relative rounded-xl p-4 border bg-[rgba(255,187,152,0.2)] border-[rgba(255,187,152,0.4)]">
              <div className="flex gap-3">
                <div className="shrink-0 mt-0.5 text-[#FFBB98]"><Heart size={20} strokeWidth={1.5} /></div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm mb-1 text-[#1D6B6B]">Warning</h4>
                  <div className="text-sm text-[#2A8585]">It&apos;s been a while since your last check-in.</div>
                </div>
              </div>
            </div>
            <div role="alert" className="relative rounded-xl p-4 border bg-[rgba(255,187,152,0.15)] border-[rgba(255,187,152,0.3)]">
              <div className="flex gap-3">
                <div className="shrink-0 mt-0.5 text-[#FFBB98]"><Heart size={20} strokeWidth={1.5} /></div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm mb-1 text-[#1D6B6B]">Gentle Alert (No red!)</h4>
                  <div className="text-sm text-[#2A8585]">Sometimes we need extra support. That&apos;s completely normal.</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <hr className="border-none h-px w-full bg-[#FFBB98] my-6" />

        <section className="py-12">
          <h2 className="font-heading font-semibold tracking-tight text-[#1D6B6B] text-2xl md:text-3xl mb-6">Badges & Icons</h2>
          <div className="flex flex-col gap-6">
            <div>
              <label className="block font-medium text-sm text-[#1D6B6B] mb-4">Badges</label>
              <div className="flex items-center gap-4 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full bg-[#FFBB98] text-[#2A8585]">Neutral</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full bg-[rgba(52,70,72,0.15)] text-[#1D6B6B]">Sage</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full bg-[rgba(255,187,152,0.15)] text-[#FFBB98]">Rose</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full bg-[rgba(125,142,149,0.15)] text-[#7D8E95]">Lavender</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full bg-[rgba(125,142,149,0.2)] text-[#7D8E95]">Blue</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full bg-[rgba(255,187,152,0.2)] text-[#FFBB98]">Gold</span>
              </div>
            </div>
            <div>
              <label className="block font-medium text-sm text-[#1D6B6B] mb-4">Icon Containers</label>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[rgba(52,70,72,0.12)] text-[#1D6B6B]"><Heart size={20} /></div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[rgba(255,187,152,0.15)] text-[#FFBB98]"><Users size={20} /></div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[rgba(125,142,149,0.15)] text-[#7D8E95]"><MessageCircle size={20} /></div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#FFBB98] text-[#2A8585]"><Settings size={20} /></div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <CoRegulateMark size={48} />
              <p className="text-base text-[#2A8585] italic text-center">Made for ADHD hearts, shaped by ADHD minds.</p>
              <span className="text-sm text-[#7D8E95]">Component Library v1.0</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
